import { Observable, ServerParseError } from '@apollo/client';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import { ErrorLink } from '@apollo/client/link/error';
import { IdToken } from '@auth0/auth0-react';
import { FormattedExecutionResult, GraphQLFormattedError } from 'graphql';
import { ObjMap } from 'graphql/jsutils/ObjMap';

import { environment } from '@anyx/app-core/env';

import { ErrorsRoutePath } from '@anyx/common-routing';
import { ObjectUtils } from '@anyx/shared-utils';



import { showSubServiceErrorNotification } from './subserviceErrorNotifier';

type ErrorSetting = {
  handleError: ({
    error,
    response,
  }: {
    error: GraphQLFormattedError;
    response: FormattedExecutionResult<ObjMap<unknown>, ObjMap<unknown>> | undefined;
  }) => void;
};

type MessageErrorSetting = Record<string, ErrorSetting>;

/**
 * If we can get the service name from the error,
 * we show the notification.
 */
const handleSubServiceError = ({ error }: { error: GraphQLFormattedError }) => {
  const serviceName = (error?.extensions?.['serviceName'] as string) || '';

  if (serviceName === 'anychat-graphql') return;

  if (serviceName) showSubServiceErrorNotification(serviceName);
};

const errorSetting: Partial<MessageErrorSetting> = {
  UNAUTHORIZED: {
    handleError: ({ response = {}, error }) => {
      if (!ObjectUtils.isEmpty(response.data)) {
        handleSubServiceError({ error });

        return;
      }

      if (window.location.pathname !== ErrorsRoutePath().unauthorized().path) {
        window.location.href = ErrorsRoutePath().unauthorized().path;
      }
    },
  },
};

type GetErrorSettingByMessage = (message: string) => ErrorSetting;
/**
 * Use the error message to find the related error handler from ErrorSetting.
 * If we can't find it, we returns a default one to make it more defensive,
 * in case BE response the error message that FE doesn't know it or doesn't handle it.
 */
const getErrorSettingByMessage: GetErrorSettingByMessage = (message) =>
  errorSetting[message] || {
    handleError: ({ response = {}, error }) => {
      if (!ObjectUtils.isEmpty(response.data)) {
        handleSubServiceError({ error });

        return;
      }
    },
  };

type ErrorLinkProps = {
  silentLogin: () => Promise<(string | IdToken)[] | undefined>;
};
/**
 * The middleware decides what to do when getting errors
 * Apollo Client v4: ErrorHandlerOptions changed - now uses unified `error` and `result` instead of graphQLErrors/networkError/response
 */
export const errorLink = ({ silentLogin }: ErrorLinkProps) =>
  new ErrorLink(({ error, result, operation, forward }) => {
    // Apollo Client v4: Ignore AbortError when operations are cancelled (e.g., component unmount)
    if (error instanceof DOMException && error.name === 'AbortError') {
      return;
    }

    // Apollo Client v4: Use CombinedGraphQLErrors.is() to check for GraphQL errors
    if (CombinedGraphQLErrors.is(error)) {
      const skipAuthRedirect = operation.getContext()['skipAuthRedirect'];

      error.errors.forEach((graphQLError) => {
        const message = graphQLError.message;
        const locations = graphQLError.locations;
        const path = graphQLError.path;

        if (environment.enableDebug) {
          console.error(
            `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
          );
        }

        if (skipAuthRedirect && message === 'UNAUTHORIZED') return;

        getErrorSettingByMessage(message).handleError({
          error: graphQLError,
          response: result as
            | FormattedExecutionResult<ObjMap<unknown>, ObjMap<unknown>>
            | undefined,
        });
      });

      return;
    }

    // Handle network errors
    if (error && 'response' in error) {
      const serverError = error as unknown as ServerParseError;
      if (serverError.response?.url) {
        const serverUrl = new URL(serverError.response.url);
        const graphQLUrl = new URL(environment.api.graphql);

        if (serverUrl.origin === graphQLUrl.origin && serverError.statusCode === 401) {
          return new Observable((observer) => {
            silentLogin()
              .then((tokens) => {
                const authorization = tokens && tokens[0];
                const oldHeaders = operation.getContext()['headers'];

                operation.setContext({
                  headers: {
                    ...oldHeaders,
                    authorization: `Bearer ${authorization}`,
                  },
                });

                forward(operation).subscribe({
                  next: observer.next.bind(observer),
                  error: observer.error.bind(observer),
                  complete: observer.complete.bind(observer),
                });
              })
              .catch((err) => {
                observer.error(err);
              });
          });
        }
      }
    }

    if (environment.enableDebug && error) {
      console.error(`[Network error]: ${error}`);
    }

    return;
  });
